import React, { useState, useEffect } from 'react';
import { Button, Icon } from '@/components/atoms';
import { Input } from '@kisanlink/ui-commons';
import {
  useCreateGRNMutation,
  usePurchaseOrderDetail,
  useWarehousesList,
  useUpdatePurchaseOrderStatusMutation,
  usePurchaseOrdersList,
} from '@/hooks/useErpApi';
import { useAppDispatch } from '@/hooks';
import { addGRN } from '@/features/grns';
import type { CreateGRNItem } from '@/schemas/grn';
import { erpClient } from '@/services/clients';

interface GRNFormProps {
  purchaseOrderId?: string;
  onSuccess?: (grnId: string) => void;
  onCancel?: () => void;
}

type DeliveryPattern = 'accept-all' | 'simple' | 'detailed';

export const GRNForm: React.FC<GRNFormProps> = ({ purchaseOrderId, onSuccess, onCancel }) => {
  const dispatch = useAppDispatch();
  const { mutateAsync: createGRN, isLoading } = useCreateGRNMutation();
  const { mutateAsync: updatePOStatus, isLoading: updatingPOStatus } =
    useUpdatePurchaseOrderStatusMutation();

  // Header fields
  const [selectedPOId, setSelectedPOId] = useState(purchaseOrderId || '');
  const [grnNumber, setGrnNumber] = useState('');
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().split('T')[0]);
  const [receivedBy, setReceivedBy] = useState('');
  const [qualityNotes, setQualityNotes] = useState('');

  // Only fetch PO details when a PO is selected
  const {
    data: purchaseOrder,
    isLoading: loadingPO,
    refetch: refetchPO,
  } = usePurchaseOrderDetail(selectedPOId || '');
  const { data: warehouses } = useWarehousesList();
  const { data: availablePOs } = usePurchaseOrdersList({ status: 'approved' });

  const [error, setError] = useState<string | null>(null);
  const [deliveryPattern, setDeliveryPattern] = useState<DeliveryPattern>('accept-all');

  // Combined loading state for both PO status update and GRN creation
  const isBusy = isLoading || updatingPOStatus;

  // Pattern 1: Accept All
  const [defaultExpiryDate, setDefaultExpiryDate] = useState('');

  // Pattern 2: Simple Accept/Reject
  const [simpleItems, setSimpleItems] = useState<
    Array<{
      po_item_id: string;
      accept: boolean;
      expiry_date: string;
      rejection_reason: string;
    }>
  >([]);

  // Pattern 3: Detailed Quantities
  const [detailedItems, setDetailedItems] = useState<
    Array<{
      po_item_id: string;
      received_quantity: number;
      accepted_quantity: number;
      expiry_date: string;
      rejection_reason: string;
    }>
  >([]);

  // Initialize warehouse from PO
  useEffect(() => {
    if (purchaseOrder && !selectedWarehouseId) {
      setSelectedWarehouseId(purchaseOrder.warehouse_id);
    }
  }, [purchaseOrder, selectedWarehouseId]);

  // Initialize items when PO loads (only once when items change)
  useEffect(() => {
    if (purchaseOrder && simpleItems.length === 0) {
      setSimpleItems(
        purchaseOrder.items.map((item) => ({
          po_item_id: item.id,
          accept: true,
          expiry_date: '',
          rejection_reason: '',
        }))
      );

      setDetailedItems(
        purchaseOrder.items.map((item) => ({
          po_item_id: item.id,
          received_quantity: item.quantity,
          accepted_quantity: item.quantity,
          expiry_date: '',
          rejection_reason: '',
        }))
      );
    }
  }, [purchaseOrder, simpleItems.length]);

  /**
   * Ensures the purchase order status is "delivered" before creating GRN
   * If the PO is not in delivered status, updates it first and verifies the change
   */
  const ensurePODelivered = async (): Promise<void> => {
    console.log('[GRN] ensurePODelivered - START');

    if (!purchaseOrder) {
      console.error('[GRN] Purchase order not loaded');
      throw new Error('Purchase order not loaded');
    }

    console.log('[GRN] Current PO status:', purchaseOrder.status);

    // If already delivered, nothing to do
    if (purchaseOrder.status === 'delivered') {
      console.log('[GRN] PO already delivered, skipping status update');
      return;
    }

    // Update PO status to delivered
    try {
      console.log('[GRN] Updating PO status to delivered...', {
        id: selectedPOId,
        status: 'delivered',
      });

      const updateResult = await updatePOStatus({
        id: selectedPOId,
        data: { status: 'delivered' },
      });

      console.log('[GRN] Update PO status result:', updateResult);

      // Fetch the updated PO directly to verify the status change
      console.log('[GRN] Verifying PO status update...');
      const response = await erpClient.purchaseOrders.get(selectedPOId);
      const updatedPO = response.data;

      console.log('[GRN] Fetched updated PO:', {
        id: updatedPO?.id,
        status: updatedPO?.status,
      });

      if (!updatedPO) {
        console.error('[GRN] Failed to verify purchase order after status update');
        throw new Error('Failed to verify purchase order after status update');
      }

      if (updatedPO.status !== 'delivered') {
        console.error('[GRN] PO status verification failed:', {
          expected: 'delivered',
          actual: updatedPO.status,
        });
        throw new Error(
          `Purchase order status is "${updatedPO.status}" but must be "delivered" to create GRN. Please update the status manually and try again.`
        );
      }

      console.log('[GRN] PO status successfully updated to delivered');

      // Trigger refetch to update the UI with the new status
      await refetchPO();
      console.log('[GRN] ensurePODelivered - COMPLETE');
    } catch (err) {
      console.error('[GRN] ensurePODelivered - ERROR:', err);

      // If it's already our custom error, re-throw it
      if (err instanceof Error && err.message.includes('must be')) {
        throw err;
      }

      // Otherwise, wrap it
      throw new Error(
        `Failed to update purchase order status to delivered: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  };

  const handleAcceptAllSubmit = async () => {
    console.log('[GRN] handleAcceptAllSubmit - START');

    if (!grnNumber.trim()) {
      setError('GRN number is required');
      return;
    }
    if (!receivedBy.trim()) {
      setError('Received by is required');
      return;
    }
    if (!defaultExpiryDate) {
      setError('Default expiry date is required');
      return;
    }

    try {
      setError(null);

      console.log('[GRN] Calling ensurePODelivered...');
      // Ensure PO status is "delivered" before creating GRN
      await ensurePODelivered();
      console.log('[GRN] ensurePODelivered completed successfully');

      const items: CreateGRNItem[] = purchaseOrder!.items.map((item) => ({
        po_item_id: item.id,
        received_quantity: item.quantity,
        accepted_quantity: item.quantity,
        rejected_quantity: 0,
        expiry_date: defaultExpiryDate,
      }));

      console.log('[GRN] Creating GRN with payload:', {
        po_id: selectedPOId,
        grn_number: grnNumber,
        received_date: receivedDate,
        received_by: receivedBy,
        quality_status: 'accepted',
        items_count: items.length,
      });

      const result = await createGRN({
        po_id: selectedPOId,
        grn_number: grnNumber,
        received_date: receivedDate,
        received_by: receivedBy,
        quality_status: 'accepted',
        remarks: qualityNotes || undefined,
        items,
      });

      console.log('[GRN] GRN created successfully:', result);
      dispatch(addGRN(result as never));
      onSuccess?.(result.id as string);
    } catch (err) {
      console.error('[GRN] handleAcceptAllSubmit - ERROR:', err);
      setError(err instanceof Error ? err.message : 'Failed to create GRN');
    }
  };

  const handleSimpleSubmit = async () => {
    console.log('[GRN] handleSimpleSubmit - START');

    if (!grnNumber.trim()) {
      setError('GRN number is required');
      return;
    }
    if (!receivedBy.trim()) {
      setError('Received by is required');
      return;
    }

    const acceptedItems = simpleItems.filter((item) => item.accept);
    const rejectedItems = simpleItems.filter((item) => !item.accept);

    if (acceptedItems.length === 0 && rejectedItems.length === 0) {
      setError('At least one item must be processed');
      return;
    }

    const hasExpiryErrors = acceptedItems.some((item) => !item.expiry_date);
    if (hasExpiryErrors) {
      setError('Expiry date is required for all accepted items');
      return;
    }

    const hasRejectionErrors = rejectedItems.some((item) => !item.rejection_reason.trim());
    if (hasRejectionErrors) {
      setError('Rejection reason is required for all rejected items');
      return;
    }

    try {
      setError(null);

      console.log('[GRN] Calling ensurePODelivered...');
      // Ensure PO status is "delivered" before creating GRN
      await ensurePODelivered();
      console.log('[GRN] ensurePODelivered completed successfully');

      const items: CreateGRNItem[] = simpleItems.map((simpleItem) => {
        const poItem = purchaseOrder!.items.find((i) => i.id === simpleItem.po_item_id)!;
        return {
          po_item_id: simpleItem.po_item_id,
          received_quantity: poItem.quantity,
          accepted_quantity: simpleItem.accept ? poItem.quantity : 0,
          rejected_quantity: simpleItem.accept ? 0 : poItem.quantity,
          expiry_date: simpleItem.expiry_date,
        };
      });

      const qualityStatus =
        acceptedItems.length === simpleItems.length
          ? 'accepted'
          : rejectedItems.length === simpleItems.length
            ? 'rejected'
            : 'partial';

      console.log('[GRN] Creating GRN with payload:', {
        po_id: selectedPOId,
        grn_number: grnNumber,
        received_date: receivedDate,
        received_by: receivedBy,
        quality_status: qualityStatus,
        items_count: items.length,
      });

      const result = await createGRN({
        po_id: selectedPOId,
        grn_number: grnNumber,
        received_date: receivedDate,
        received_by: receivedBy,
        quality_status: qualityStatus,
        remarks: qualityNotes || undefined,
        items,
      });

      console.log('[GRN] GRN created successfully:', result);
      dispatch(addGRN(result as never));
      onSuccess?.(result.id as string);
    } catch (err) {
      console.error('[GRN] handleSimpleSubmit - ERROR:', err);
      setError(err instanceof Error ? err.message : 'Failed to create GRN');
    }
  };

  const handleDetailedSubmit = async () => {
    if (!grnNumber.trim()) {
      setError('GRN number is required');
      return;
    }
    if (!receivedBy.trim()) {
      setError('Received by is required');
      return;
    }

    const hasExpiryErrors = detailedItems.some(
      (item) => item.accepted_quantity > 0 && !item.expiry_date
    );
    if (hasExpiryErrors) {
      setError('Expiry date is required for items with accepted quantity');
      return;
    }

    const hasQuantityErrors = detailedItems.some(
      (item) => item.accepted_quantity > item.received_quantity
    );
    if (hasQuantityErrors) {
      setError('Accepted quantity cannot exceed received quantity');
      return;
    }

    const hasRejectionErrors = detailedItems.some(
      (item) => item.received_quantity - item.accepted_quantity > 0 && !item.rejection_reason.trim()
    );
    if (hasRejectionErrors) {
      setError('Rejection reason is required when accepted < received');
      return;
    }

    try {
      setError(null);

      // Ensure PO status is "delivered" before creating GRN
      await ensurePODelivered();

      const items: CreateGRNItem[] = detailedItems.map((item) => ({
        po_item_id: item.po_item_id,
        received_quantity: item.received_quantity,
        accepted_quantity: item.accepted_quantity,
        rejected_quantity: item.received_quantity - item.accepted_quantity,
        expiry_date: item.expiry_date,
      }));

      const totalAccepted = items.reduce((sum, item) => sum + item.accepted_quantity, 0);
      const totalRejected = items.reduce((sum, item) => sum + (item.rejected_quantity || 0), 0);
      const qualityStatus =
        totalRejected === 0 ? 'accepted' : totalAccepted === 0 ? 'rejected' : 'partial';

      const result = await createGRN({
        po_id: selectedPOId,
        grn_number: grnNumber,
        received_date: receivedDate,
        received_by: receivedBy,
        quality_status: qualityStatus,
        remarks: qualityNotes || undefined,
        items,
      });

      dispatch(addGRN(result as never));
      onSuccess?.(result.id as string);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create GRN');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (deliveryPattern === 'accept-all') {
      await handleAcceptAllSubmit();
    } else if (deliveryPattern === 'simple') {
      await handleSimpleSubmit();
    } else {
      await handleDetailedSubmit();
    }
  };

  // Show loading only when actually fetching a selected PO
  if (loadingPO && selectedPOId) {
    return (
      <div className="flex items-center justify-center p-8">
        <Icon name="Package" size={48} className="text-neutral-400 mx-auto mb-4" />
        <p className="text-lg font-medium text-neutral-600">Loading purchase order...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-error-50 border border-error-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Icon name="CircleAlert" size={20} className="text-error-600 mt-0.5" />
            <p className="text-error-700">{error}</p>
          </div>
        </div>
      )}

      {/* Step 1: GRN Header */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-neutral-900">GRN Information</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              GRN Number (from vendor) *
            </label>
            <Input
              type="text"
              value={grnNumber}
              onChange={(e) => setGrnNumber(e.target.value)}
              placeholder="Enter GRN number from vendor document"
              disabled={isBusy}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Purchase Order *
            </label>
            {purchaseOrderId ? (
              <Input
                type="text"
                value={purchaseOrder?.po_number || ''}
                disabled
                className="bg-neutral-100"
              />
            ) : (
              <select
                value={selectedPOId}
                onChange={(e) => setSelectedPOId(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                disabled={isBusy}
                required
              >
                <option value="">Select a purchase order</option>
                {availablePOs?.map((po) => (
                  <option key={po.id} value={po.id}>
                    {po.po_number} - {po.collaborator_name} ({po.status})
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Warehouse *</label>
            <select
              value={selectedWarehouseId}
              onChange={(e) => setSelectedWarehouseId(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              disabled={isBusy}
              required
            >
              <option value="">Select warehouse</option>
              {warehouses?.map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Received Date *
            </label>
            <Input
              type="date"
              value={receivedDate}
              onChange={(e) => setReceivedDate(e.target.value)}
              disabled={isBusy}
              required
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-neutral-700 mb-2">Received By *</label>
            <Input
              type="text"
              value={receivedBy}
              onChange={(e) => setReceivedBy(e.target.value)}
              placeholder="Name of person receiving goods"
              disabled={isBusy}
              required
            />
          </div>
        </div>
      </div>

      {/* Show helper message when no PO is selected */}
      {!selectedPOId && (
        <div className="bg-info-50 border border-info-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={24} className="text-info-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-info-900 mb-1">Select a Purchase Order</h4>
              <p className="text-sm text-info-700">
                Choose a purchase order from the dropdown above to start creating a GRN. Only
                approved purchase orders are shown.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Only show delivery pattern and items when a PO is selected and loaded */}
      {selectedPOId && purchaseOrder && (
        <>
          {/* Step 2: Delivery Pattern Selection */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-neutral-900">Delivery Pattern</h3>
            <p className="text-sm text-neutral-600">
              Choose how you want to record the received items
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                type="button"
                onClick={() => setDeliveryPattern('accept-all')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  deliveryPattern === 'accept-all'
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-neutral-200 hover:border-neutral-300'
                }`}
                disabled={isBusy}
              >
                <Icon
                  name="CircleCheck"
                  size={24}
                  className={`mx-auto mb-2 ${
                    deliveryPattern === 'accept-all' ? 'text-primary-600' : 'text-neutral-400'
                  }`}
                />
                <p className="font-semibold text-neutral-900">Accept All</p>
                <p className="text-xs text-neutral-600 mt-1">One-click, all items accepted</p>
              </button>

              <button
                type="button"
                onClick={() => setDeliveryPattern('simple')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  deliveryPattern === 'simple'
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-neutral-200 hover:border-neutral-300'
                }`}
                disabled={isBusy}
              >
                <Icon
                  name="List"
                  size={24}
                  className={`mx-auto mb-2 ${
                    deliveryPattern === 'simple' ? 'text-primary-600' : 'text-neutral-400'
                  }`}
                />
                <p className="font-semibold text-neutral-900">Simple Accept/Reject</p>
                <p className="text-xs text-neutral-600 mt-1">Yes/No per item</p>
              </button>

              <button
                type="button"
                onClick={() => setDeliveryPattern('detailed')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  deliveryPattern === 'detailed'
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-neutral-200 hover:border-neutral-300'
                }`}
                disabled={isBusy}
              >
                <Icon
                  name="ClipboardList"
                  size={24}
                  className={`mx-auto mb-2 ${
                    deliveryPattern === 'detailed' ? 'text-primary-600' : 'text-neutral-400'
                  }`}
                />
                <p className="font-semibold text-neutral-900">Detailed Quantities</p>
                <p className="text-xs text-neutral-600 mt-1">Specify received/accepted amounts</p>
              </button>
            </div>
          </div>

          {/* Step 3: Item-Level Data Entry */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-neutral-900">Items</h3>

            {deliveryPattern === 'accept-all' && (
              <div className="bg-success-50 border border-success-200 rounded-lg p-6">
                <div className="flex items-start gap-3 mb-4">
                  <Icon name="CircleCheck" size={24} className="text-success-600" />
                  <div>
                    <p className="font-semibold text-success-900">Accept All Items</p>
                    <p className="text-sm text-success-700 mt-1">
                      All {purchaseOrder.items.length} items will be accepted with the default
                      expiry date
                    </p>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Default Expiry Date *
                  </label>
                  <Input
                    type="date"
                    value={defaultExpiryDate}
                    onChange={(e) => setDefaultExpiryDate(e.target.value)}
                    disabled={isBusy}
                    required
                  />
                  <p className="text-xs text-neutral-600 mt-1">
                    This expiry date will be applied to all items
                  </p>
                </div>

                <div className="mt-4 max-h-60 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-100 sticky top-0">
                      <tr>
                        <th className="text-left py-2 px-3">Product</th>
                        <th className="text-right py-2 px-3">Quantity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchaseOrder.items.map((item) => (
                        <tr key={item.id} className="border-b border-neutral-200">
                          <td className="py-2 px-3">{item.product_name}</td>
                          <td className="py-2 px-3 text-right">{item.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {deliveryPattern === 'simple' && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-neutral-100">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">
                        Product
                      </th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">
                        Quantity
                      </th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">
                        Accept?
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">
                        Expiry Date
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">
                        Rejection Reason
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchaseOrder.items.map((poItem, index) => {
                      const simpleItem = simpleItems[index];
                      return (
                        <tr key={poItem.id} className="border-b border-neutral-200">
                          <td className="py-3 px-4">
                            <p className="font-medium text-neutral-900">{poItem.product_name}</p>
                          </td>
                          <td className="py-3 px-4 text-center">{poItem.quantity}</td>
                          <td className="py-3 px-4 text-center">
                            <input
                              type="checkbox"
                              checked={simpleItem.accept}
                              onChange={(e) => {
                                const newItems = [...simpleItems];
                                newItems[index].accept = e.target.checked;
                                setSimpleItems(newItems);
                              }}
                              className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                              disabled={isBusy}
                            />
                          </td>
                          <td className="py-3 px-4">
                            {simpleItem.accept && (
                              <Input
                                type="date"
                                value={simpleItem.expiry_date}
                                onChange={(e) => {
                                  const newItems = [...simpleItems];
                                  newItems[index].expiry_date = e.target.value;
                                  setSimpleItems(newItems);
                                }}
                                disabled={isBusy}
                                required
                              />
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {!simpleItem.accept && (
                              <Input
                                type="text"
                                value={simpleItem.rejection_reason}
                                onChange={(e) => {
                                  const newItems = [...simpleItems];
                                  newItems[index].rejection_reason = e.target.value;
                                  setSimpleItems(newItems);
                                }}
                                placeholder="Enter reason"
                                disabled={isBusy}
                                required
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {deliveryPattern === 'detailed' && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-neutral-100">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">
                        Product
                      </th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">
                        Ordered
                      </th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">
                        Received
                      </th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">
                        Accepted
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">
                        Expiry Date
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">
                        Rejection Reason
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchaseOrder.items.map((poItem, index) => {
                      const detailedItem = detailedItems[index];
                      const rejectedQty =
                        detailedItem.received_quantity - detailedItem.accepted_quantity;
                      return (
                        <tr key={poItem.id} className="border-b border-neutral-200">
                          <td className="py-3 px-4">
                            <p className="font-medium text-neutral-900">{poItem.product_name}</p>
                          </td>
                          <td className="py-3 px-4 text-center">{poItem.quantity}</td>
                          <td className="py-3 px-4">
                            <Input
                              type="number"
                              value={detailedItem.received_quantity}
                              onChange={(e) => {
                                const newItems = [...detailedItems];
                                newItems[index].received_quantity = parseInt(e.target.value) || 0;
                                // Auto-adjust accepted if received is reduced
                                if (
                                  newItems[index].accepted_quantity >
                                  newItems[index].received_quantity
                                ) {
                                  newItems[index].accepted_quantity =
                                    newItems[index].received_quantity;
                                }
                                setDetailedItems(newItems);
                              }}
                              min={0}
                              max={poItem.quantity}
                              disabled={isBusy}
                              className="w-24 text-center"
                              required
                            />
                          </td>
                          <td className="py-3 px-4">
                            <Input
                              type="number"
                              value={detailedItem.accepted_quantity}
                              onChange={(e) => {
                                const newItems = [...detailedItems];
                                newItems[index].accepted_quantity = parseInt(e.target.value) || 0;
                                setDetailedItems(newItems);
                              }}
                              min={0}
                              max={detailedItem.received_quantity}
                              disabled={isBusy}
                              className="w-24 text-center"
                              required
                            />
                          </td>
                          <td className="py-3 px-4">
                            {detailedItem.accepted_quantity > 0 && (
                              <Input
                                type="date"
                                value={detailedItem.expiry_date}
                                onChange={(e) => {
                                  const newItems = [...detailedItems];
                                  newItems[index].expiry_date = e.target.value;
                                  setDetailedItems(newItems);
                                }}
                                disabled={isBusy}
                                required
                              />
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {rejectedQty > 0 && (
                              <Input
                                type="text"
                                value={detailedItem.rejection_reason}
                                onChange={(e) => {
                                  const newItems = [...detailedItems];
                                  newItems[index].rejection_reason = e.target.value;
                                  setDetailedItems(newItems);
                                }}
                                placeholder="Enter reason"
                                disabled={isBusy}
                                required
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Step 4: Quality Inspection */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-neutral-900">
              Quality Inspection (Optional)
            </h3>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Quality Notes
              </label>
              <textarea
                value={qualityNotes}
                onChange={(e) => setQualityNotes(e.target.value)}
                placeholder="Enter any quality inspection notes, observations, or remarks..."
                rows={4}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                disabled={isBusy}
              />
            </div>
          </div>
        </>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4 border-t border-neutral-200">
        <Button
          type="submit"
          variant="primary"
          disabled={isBusy || !selectedPOId || !purchaseOrder}
          className="flex-1"
        >
          {isBusy ? (
            <>
              <Icon name="Loader" size={20} className="mr-2 animate-spin" />
              {updatingPOStatus ? 'Updating PO Status...' : 'Creating GRN...'}
            </>
          ) : (
            <>
              <Icon name="Check" size={20} className="mr-2" />
              Create GRN
            </>
          )}
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel} disabled={isBusy}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
};
